import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Alert,
  Button,
  ConfigProvider,
  Empty,
  Select,
  Skeleton,
  Space,
  Spin,
  message,
} from "antd";
import {
  EditOutlined,
  FileExcelOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { ThreeMPhaseNode } from "../ThreeMConsolidation/threeMShared";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";
import ExecutionKpiCards from "./ExecutionKpiCards";
import ExecutionCharts from "./ExecutionCharts";
import ExecutionTopSheetTable from "./ExecutionTopSheetTable";
import ClientPlanModal from "./ClientPlanModal";
import {
  ExecutionRowKind,
  ExecutionSummaryData,
  FyOption,
  buildFyOptions,
  fmtMonth,
  fyOf,
  fyOption,
  kindToTab,
  num,
} from "./executionShared";

// Monthly Execution Summary — the planned-vs-executed twin of the Monthly P&L
// Top Sheet: Planned Revenue (Client) vs Planned Expenditure vs Executed
// Expenditure per month, with the carry-over waterfall (a month's shortfall
// becomes the next month's opening backlog) and the 3M pillars behind each
// expenditure figure. Executed Expenditure is actual quantity valued at PLANNED
// rates — a cost, deliberately not called revenue; actual consumed cost is a
// separate (not yet built) tier. Clicking a month amount opens the Month Detail
// page (separate screen — a month can touch 900+ tasks).

const ALL_MONTHS = "__ALL__";

const MonthlyExecutionReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<ExecutionSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const wbsName = useWbsName();
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  const currentFy = fyOf(dayjs().format("YYYY-MM-01"));
  const [fyKey, setFyKey] = useState<string>(String(currentFy));

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [clientPlanOpen, setClientPlanOpen] = useState(false);

  // Fullscreen kit (pattern: MonthlyPnl) — popups re-target into the container.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  useEffect(() => {
    if ((location.state as any)?.fullscreen && !document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {
        /* no gesture left — degrade to normal view */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Phase/Activity options from one unfiltered consolidation load.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    threeMReportApis
      .get3MConsolidation(projectId)
      .then((response) => {
        if (!cancelled && response?.status) {
          setBasePhases(response?.data?.phases || []);
        }
      })
      .catch((err) =>
        console.error("Error loading phase/activity filter options:", err)
      );
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const fyWindow = useMemo((): { fromMonth?: string; toMonth?: string } => {
    if (fyKey === ALL_MONTHS) return {};
    const option = fyOption(Number(fyKey));
    return { fromMonth: option.fromMonth, toMonth: option.toMonth };
  }, [fyKey]);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.getMonthlyExecutionSummary(projectId, {
          ...fyWindow,
          phaseId,
          activityId,
        });
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
        } else {
          setError(response?.message || "Failed to load the Monthly Execution Summary");
          message.error(response?.message || "Failed to load the Monthly Execution Summary");
        }
      } catch (err) {
        console.error("Error loading Monthly Execution Summary:", err);
        if (!cancelled) {
          setError("Error loading Monthly Execution Summary");
          message.error("Error loading Monthly Execution Summary");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, fyWindow, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";
  const months = useMemo(() => data?.months || [], [data]);
  // 'YYYY-MM' -> the schedules-derived amount, so the entry modal can show what each
  // month would use if left out. Keyed to the modal's month format, not 'YYYY-MM-01'.
  const derivedByMonth = useMemo(
    () =>
      months.reduce<Record<string, number>>((acc, m) => {
        if (m.month && m.month !== "TOTAL" && m.clientPlanDerivedAmount !== undefined) {
          acc[m.month.slice(0, 7)] = num(m.clientPlanDerivedAmount);
        }
        return acc;
      }, {}),
    [months]
  );
  const lastActualMonth = data?.scheduleRange?.lastActualMonth || "";
  const noActualsYet = months.length > 0 && !lastActualMonth;

  const fyOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = buildFyOptions(
      data?.scheduleRange?.firstMonth || undefined,
      data?.scheduleRange?.lastMonth || undefined
    ).map((option: FyOption) => ({
      label: option.label,
      value: String(fyOf(option.fromMonth)),
    }));
    if (!options.some((option) => option.value === String(currentFy))) {
      options.push({ label: fyOption(currentFy).label, value: String(currentFy) });
      options.sort((a, b) => Number(a.value) - Number(b.value));
    }
    options.push({ label: "Entire project", value: ALL_MONTHS });
    return options;
  }, [data, currentFy]);

  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases, wbsName]
  );
  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  // The table hands over a row KIND, the chart drill hands over a TAB — both
  // land on the same Month Detail screen.
  const openMonthDetailTab = (month: string, tab: string) => {
    navigate(
      `/pms/project/monthly-execution-month/${projectId}?month=${month}&tab=${tab}`,
      {
        state: {
          ...(location.state || {}),
          id: projectId,
          projectName,
          phaseId,
          activityId,
          fullscreen: isFullscreen,
        },
      }
    );
  };

  const openMonthDetail = (month: string, kind: ExecutionRowKind) =>
    openMonthDetailTab(month, kindToTab(kind));

  // Chart → Analysis page. The window and filters go in the QUERY so the page
  // reloads the same figures the clicked card was showing, even on a cold link.
  const openAnalysis = (
    view: "target-executed" | "outflow",
    month?: string | null,
    measure?: string
  ) => {
    const query = new URLSearchParams({ view });
    if (month) query.set("month", month);
    if (measure && measure !== "total") query.set("measure", measure);
    if (fyWindow.fromMonth) query.set("fromMonth", fyWindow.fromMonth);
    if (fyWindow.toMonth) query.set("toMonth", fyWindow.toMonth);
    if (phaseId) query.set("phaseId", String(phaseId));
    if (activityId) query.set("activityId", String(activityId));
    navigate(
      `/pms/project/monthly-execution-analysis/${projectId}?${query.toString()}`,
      {
        state: {
          ...(location.state || {}),
          id: projectId,
          projectName,
          fullscreen: isFullscreen,
        },
      }
    );
  };

  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? basePhases.find((p) => p.phaseId === phaseId)?.phaseName
        : undefined;
      const activityName =
        phaseId && activityId
          ? basePhases
              .find((p) => p.phaseId === phaseId)
              ?.activities?.find((a) => a.activityId === activityId)?.activityName
          : undefined;
      await exportWorkbook({
        data: { ...data, projectName },
        orgName,
        logo,
        fyLabel:
          fyKey === ALL_MONTHS ? "Entire project" : fyOption(Number(fyKey)).label,
        filters: { phaseName, activityName },
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Monthly Execution Summary:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Head title="Monthly Execution" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "nowrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  Monthly Execution
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Planned revenue vs planned vs executed expenditure —
                  shortfalls carry into the next month's opening backlog
                </span>
              </div>
              {/* Pinned to the top-right, on the title's line: the header row
                  never wraps and the title block shrinks instead (minWidth:0),
                  so the actions can't be pushed onto a row of their own. The
                  Space still wraps INTERNALLY on very narrow viewports. */}
              <Space wrap style={{ marginLeft: "auto", flexShrink: 0, justifyContent: "flex-end" }}>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setClientPlanOpen(true)}
                >
                  Edit Planned Revenue
                </Button>
                <Button
                  icon={
                    isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || months.length === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              <Select
                style={{ width: 170 }}
                options={fyOptions}
                value={fyKey}
                onChange={(value) => setFyKey(value)}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              {(phaseId || activityId) && (
                <Button
                  type="link"
                  onClick={() => {
                    setPhaseId(undefined);
                    setActivityId(undefined);
                  }}
                >
                  Clear filters
                </Button>
              )}
              {lastActualMonth && (
                <span
                  style={{
                    marginLeft: "auto",
                    color: "#526484",
                    fontSize: 12.5,
                    alignSelf: "center",
                  }}
                >
                  Actuals captured through{" "}
                  <b style={{ color: "#022c57" }}>{fmtMonth(lastActualMonth)}</b>
                </span>
              )}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <ExecutionKpiCards data={data} />

                {noActualsYet && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="No monthly actuals captured yet"
                    description="Record actual executed quantities per task (task page → Monthly Actual Execution, or Tasks → More → Upload Monthly Actuals) and the Executed Expenditure row, month variance and carry-over backlog will light up here."
                  />
                )}

                {months.length === 0 ? (
                  <Empty
                    description={
                      fyKey === ALL_MONTHS
                        ? "No monthly schedules, actuals or planned revenue for this project yet"
                        : "Nothing in this fiscal year — try another FY or 'Entire project'"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <>
                    <ExecutionCharts months={months} onOpenAnalysis={openAnalysis} />
                    <ExecutionTopSheetTable
                      months={months}
                      totals={data?.totals}
                      onCellClick={openMonthDetail}
                    />
                    <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                      Planned Revenue (Client) = billing program (scheduled qty × Basic
                      Rate, net of GST and labour cess; muted months are derived, not
                      entered); Planned Expenditure = planned execution cost (3M +
                      Equipment). In the Execution section each month splits into
                      Target (the same cost basis × actual quantities — valued at
                      planned rates), Actual (real consumed cost from the daily site
                      captures: material issues, equipment logs, time logs) and
                      Deviation = Target − Actual (positive = under cost). Actual
                      Client Billing = Daily Execution quantities × Basic Rate — click
                      it for the activity-wise running-meter breakdown. The Total
                      column's backlog cells are positional: opening of the first
                      visible month, closing of the last actualized one.
                    </p>
                  </>
                )}
              </Spin>
            )}
          </div>
        </Card>
        </ConfigProvider>
        </div>
      </Content>

      <ClientPlanModal
        open={clientPlanOpen}
        onClose={() => setClientPlanOpen(false)}
        projectId={projectId}
        onSaved={() => setRefreshTick((t) => t + 1)}
        derivedByMonth={derivedByMonth}
      />
    </>
  );
};

export default MonthlyExecutionReport;
